import React, { useCallback, useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { Select } from 'antd';

import { DATA_SERVICES_SORT_OPTIONS } from '@/shared/lib/constants/options';
import type { DataServiceSortOption } from '@/shared/types/options';
import { EllipsisText } from '@/shared/ui/EllipsisText';
import { NoDataBoundary } from '@/shared/ui/NoDataBoundary';
import { ServiceIcon } from '@/shared/ui/ServiceIcon';

import type { ServiceBucket } from '@/features/data-services/api/types';
import { useDataServices } from '@/features/data-services/model/useDataServices';

import styles from './DataServicesOverview.module.css';

const nameAscSortHandler = (a: ServiceBucket, b: ServiceBucket) => a.key.localeCompare(b.key, 'ko', { numeric: true });

const SERVICE_SORT_HANDLERS: Record<DataServiceSortOption, (a: ServiceBucket, b: ServiceBucket) => number> = {
  countAsc: (a, b) => a.doc_count - b.doc_count || nameAscSortHandler(a, b),
  countDesc: (a, b) => b.doc_count - a.doc_count || nameAscSortHandler(a, b),
  nameAsc: nameAscSortHandler,
};

const getSortedServiceData = (servicesData: ServiceBucket[], sortOption: DataServiceSortOption): ServiceBucket[] => {
  if (!servicesData.length) return [];
  return [...servicesData].sort(SERVICE_SORT_HANDLERS[sortOption]);
};

export const DataServicesOverview: React.FC = () => {
  const navigate = useNavigate();

  const [sortOption, setSortOption] = useState<DataServiceSortOption>('countDesc');
  const onChangeSortOption = useCallback((value: DataServiceSortOption) => {
    setSortOption(value);
  }, []);

  const { data: servicesRaw } = useDataServices();

  const services = useMemo(() => servicesRaw.filter((s) => s.doc_count > 0), [servicesRaw]);

  const sortedServiceData = useMemo(() => {
    return getSortedServiceData(services, sortOption);
  }, [services, sortOption]);

  const handleServiceClick = useCallback(
    (serviceName: string) => {
      navigate(`/search?dataSource=${serviceName}`);
    },
    [navigate]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>, serviceName: string) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleServiceClick(serviceName);
      }
    },
    [handleServiceClick]
  );

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div className={styles.title}>소스별 자산 분포</div>
        <Select
          options={DATA_SERVICES_SORT_OPTIONS}
          value={sortOption}
          onChange={onChangeSortOption}
          className={styles.sortSelect}
        />
      </div>

      <div className={styles.content}>
        <NoDataBoundary data={services} emptyDescription="표시할 데이터가 없습니다">
          <div className={styles.servicesContainer}>
            {sortedServiceData.map((service) => (
              <div
                key={service.key}
                className={styles.serviceCard}
                onClick={() => handleServiceClick(service.key)}
                onKeyDown={(e) => handleKeyDown(e, service.key)}
                tabIndex={0}
                role="button"
                aria-label={`${service.key} 서비스 (${service.doc_count.toLocaleString()}개 자산)`}>
                <ServiceIcon serviceType={service.key} size={64} className={styles.serviceIcon} />
                <div className={styles.serviceNameContainer}>
                  <EllipsisText text={service.key} className={styles.serviceName} />
                  <span className={styles.serviceCount}>{service.doc_count.toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        </NoDataBoundary>
      </div>
    </div>
  );
};
